import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaAddressBook, FaGit, FaHackerrank, FaLetter, FaLinkedin, FaMedium, FaPhone, FaTwitter, FaVoicemail } from 'react-icons/fa';

const Contact = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [focused, setFocused] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Add your form submission logic here
  };

  const contactInfo = [
    {
      icon: <FaAddressBook/>,
      label: 'Email',
      value: 'divita.phadakale@colorado.edu',
      link: 'mailto:divita.phadakale@colorado.edu'
    },
    {
      icon: <FaPhone/>,
      label: 'Phone',
      value: '+1 (720) 354-2485',
      link: 'tel:+17203542485'
    },
    {
      icon: <FaVoicemail/>,
      label: 'Location',
      value: 'Boulder, Colorado',
      link: '#'
    }
  ];

  const socialLinks = [
    { name: 'GitHub', icon: <FaGit />, link: 'https://github.com/DivitaP', color: '#333' },
    { name: 'LinkedIn', icon: <FaLinkedin />, link: 'https://www.linkedin.com/in/divita-phadakale-304120168/', color: '#0077b5' },
    { name: 'HackerRank', icon: <FaHackerrank />, link: 'https://www.hackerrank.com/profile/divitaphadakale1', color: '#1da1f2' },
    { name: 'Medium', icon: <FaMedium />, link: 'https://medium.com/@divitaphadakale21', color: '#00ab6c' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  return (
    <section className="contact" id="contact" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="section-tag">
            <span className="tag-bracket">{'<'}</span>
            contact
            <span className="tag-bracket">{'/>'}</span>
          </span>
          <h2 className="section-title">
            <span className="keyword">Let's</span> Connect<span className="cursor-blink">.</span>
          </h2>
          <p className="section-description">
            Available for new collaborations and opportunities
          </p>
        </motion.div>

        <div className="contact-content">
          {/* Contact Info */}
          <motion.div
            className="contact-info"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <motion.div className="info-header" variants={itemVariants}>
              <h3 className="info-title">
                <span className="comment">// Direct Contact</span>
              </h3>
              <p className="info-subtitle">
                I'm always excited to discuss new opportunities. Whether you have a specific 
                project in mind or just want to chat about tech, let's build something meaningful together!
              </p>
            </motion.div>

            {contactInfo.map((info, index) => (
              <motion.a
                key={info.label}
                href={info.link}
                className="info-item"
                variants={itemVariants}
                whileHover={{ x: 10, scale: 1.02 }}
              >
                <span className="info-icon">{info.icon}</span>
                <div className="info-details">
                  <span className="info-label">{info.label}</span>
                  <span className="info-value">{info.value}</span>
                </div>
              </motion.a>
            ))}

            <motion.div className="social-links" variants={itemVariants}>
              <h4 className="social-title">Find me on</h4>
              <div className="social-grid">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.name}
                    href={social.link}
                    className="social-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    whileHover={{ 
                      scale: 1.1, 
                      y: -5,
                      backgroundColor: social.color 
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="social-icon">{social.icon}</span>
                    <span className="social-name">{social.name}</span>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="availability-badge"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <motion.div
                className="pulse-dot"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [1, 0.5, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <span>Let's chat on the latest tech trends</span>
              <p className="response-time">Response time: Usually {'<'} 24 hours</p>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="contact-form-wrapper"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <motion.div className="form-header" variants={itemVariants}>
              <h3 className="form-title">
                <span className="comment">// Initialize Transmission</span>
              </h3>
            </motion.div>

            <motion.form 
              className="contact-form" 
              onSubmit={handleSubmit}
              variants={containerVariants}
            >
              <motion.div 
                className={`form-group ${focused === 'name' ? 'focused' : ''}`}
                variants={itemVariants}
              >
                <label htmlFor="name">
                  <span className="keyword">const</span> name = 
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocused('name')}
                  onBlur={() => setFocused('')}
                  placeholder="Your Name"
                  required
                />
              </motion.div>

              <motion.div 
                className={`form-group ${focused === 'email' ? 'focused' : ''}`}
                variants={itemVariants}
              >
                <label htmlFor="email">
                  <span className="keyword">const</span> email = 
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocused('email')}
                  onBlur={() => setFocused('')}
                  placeholder="your.email@example.com"
                  required
                />
              </motion.div>

              <motion.div 
                className={`form-group ${focused === 'subject' ? 'focused' : ''}`}
                variants={itemVariants}
              >
                <label htmlFor="subject">
                  <span className="keyword">const</span> subject = 
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onFocus={() => setFocused('subject')}
                  onBlur={() => setFocused('')}
                  placeholder="Project Subject"
                  required
                />
              </motion.div>

              <motion.div 
                className={`form-group ${focused === 'message' ? 'focused' : ''}`}
                variants={itemVariants}
              >
                <label htmlFor="message">
                  <span className="keyword">const</span> message = 
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocused('message')}
                  onBlur={() => setFocused('')}
                  placeholder="Tell me about your project..."
                  rows="5"
                  required
                />
              </motion.div>

              <motion.button
                type="submit"
                className="submit-btn"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.05, 
                  boxShadow: "0 0 30px var(--glow-green)" 
                }}
                whileTap={{ scale: 0.95 }}
              >
                
                Send Message
              </motion.button>
            </motion.form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
